import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { locales, defaultLocale, ValidLocale } from './config';
import enTranslations from './locales/en.json';

interface Messages {
  [key: string]: any;  
}

interface LanguageContextType {
  currentLocale: ValidLocale;
  setLanguage: (locale: ValidLocale) => void;
  translate: (key: string) => string;
  messages: Messages;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [currentLocale, setCurrentLocale] = useState<ValidLocale>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('preferred-locale');
      return (saved && locales.includes(saved as ValidLocale)) ? saved as ValidLocale : defaultLocale;
    }
    return defaultLocale;
  });

  const [messages, setMessages] = useState<Messages>(enTranslations);

  useEffect(() => {
    loadMessages(currentLocale);
  }, [currentLocale]);

  const loadMessages = async (locale: ValidLocale) => {
    try {
      if (locale === 'en') {
        // Use the statically imported English translations
        setMessages(enTranslations);
        return;
      }
      
      // For other locales, load dynamically
      const messagesModule = await import(`./locales/${locale}.json`);
      const messageData = messagesModule.default || messagesModule;
      setMessages(messageData || enTranslations);
    } catch (error) {
      console.error(`Failed to load messages for locale: ${locale}`, error);
      // Fallback to English translations
      setMessages(enTranslations);
    }
  };

  const setLanguage = (locale: ValidLocale) => {
    if (locale !== currentLocale) {
      localStorage.setItem('preferred-locale', locale);
      setCurrentLocale(locale);
    }
  };

  const translate = (key: string): string => {
    if (!key) return '';
    
    // Handle nested keys (e.g., "index.sleepCoach.title")
    const keys = key.split('.');
    let result: any = messages;
    
    for (const k of keys) {
      if (result && typeof result === 'object' && k in result) {
        result = result[k];
      } else {
        console.error(`Translation missing for key: ${key}`, {
          component: 'LanguageContext',
          locale: currentLocale,
          key
        });
        return key;
      }
    }

    return typeof result === 'string' ? result : key;
  };

  return (
    <LanguageContext.Provider value={{ currentLocale, setLanguage, translate, messages }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
